import { FileExtension, fileTypeFromBuffer, MimeType } from "file-type";
import React, { useState, useEffect } from "react";

import UploadZone from "./UploadZone";
import { callApi } from "utils/ContentoApi";
import { convertUnsupportedImage } from "./convertImage";
import { ImageWrapper, PostImage } from "./styles";
import Loader from "components/common/loading/Loader";

const getWidthAndHeight = (
  image
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      reject("Could not load image file");
    };

    const fileReader = new FileReader();
    fileReader.onload = () => {
      // console.log("File reader:: ", fileReader);
      img.src = fileReader.result as any;
    };
    fileReader.onerror = () => {
      reject("Could not load image file");
    };
    fileReader.readAsDataURL(image);
  });
};

const getFileType = (
  image
): Promise<{ mime?: MimeType; ext?: FileExtension }> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = async () => {
      if (fileReader.result) {
        const fileType = await fileTypeFromBuffer(
          fileReader.result as ArrayBuffer
        );
        resolve({
          mime: fileType?.mime,
          ext: fileType?.ext
        });
      } else {
        reject("Could not load image file");
      }
    };
    fileReader.onerror = () => {
      reject("Could not load image file");
    };
    fileReader.readAsArrayBuffer(image);
  });
};

const getImageMetadata = async image => {
  const { width, height } = await getWidthAndHeight(image);
  const { mime, ext } = await getFileType(image);

  return {
    width,
    height,
    mime,
    ext
  };
};

interface IUploadedImage {
  metaData: any;
  preview: string;
  progress: number;
}

const ImageUpload = ({
  onError,
  onUpload,
  onCancel,
  isUploading,
  setIsUploading,
  openFileModalByDefault,
  handleScrollerVisibility
}) => {
  const [uploadedImages, setUploadedImages] = useState<IUploadedImage[]>([]);

  useEffect(() => {
    if (handleScrollerVisibility) {
      handleScrollerVisibility();
    }
  }, [uploadedImages.length, handleScrollerVisibility]);

  const onDrop = async droppedFiles => {
    setIsUploading(true);

    const images = await Promise.all(droppedFiles.map(convertUnsupportedImage));

    for (const image of images) {
      if (image.size > 5 * 1024 * 1024) {
        onError(
          new Error(
            "The selected image exceeds the 5mb limit. Please reduce it's size and try again."
          )
        );
        setIsUploading(false);
        return;
      }
    }

    const uploads = images.map(image => {
      const formData = new FormData();
      formData.append("file", image);
      formData.append("timestamp", ((Date.now() / 1000) | 0).toString());
      setUploadedImages((prevState: any) => [
        ...prevState,
        {
          metaData: image,
          preview: URL.createObjectURL(image),
          progress: 0
        }
      ]);
      return callApi({
        method: "post",
        url: "images/upload",
        data: formData,
        headers: [{ "Content-Type": "multipart/form-data" }],
        onUploadProgress: function (progressEvent) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadedImages(prevState => {
            const imageIndex = prevState.findIndex(
              img => img.metaData?.name === image.name
            );

            if (imageIndex < 0) {
              return prevState;
            }

            return [
              ...prevState.slice(0, imageIndex),
              {
                ...prevState[imageIndex],
                progress: percentCompleted
              },
              ...prevState.slice(imageIndex + 1)
            ];
          });
        }
      });
    });

    Promise.all(images.map(getImageMetadata))
      .then(imagesMetadata => {
        //we have all metadata start uploading
        return Promise.all(uploads).then(imageUploadResults => {
          //flatten uploadresults, merge with metadata
          const allImages = [].concat
            .apply([], imageUploadResults)
            .map((result: any, i) => {
              return {
                ...result,
                metaData: imagesMetadata[i]
              };
            });
          setUploadedImages([]);
          setIsUploading(false);
          onUpload(allImages);
        });
      })
      .catch(err => {
        if (err.message === "File too large") {
          onError(
            new Error(
              "The selected image exceeds the 5mb limit. Please reduce it's size and try again."
            )
          );
        } else if (err.message?.startsWith("Unsupported image type")) {
          onError(
            new Error(
              `${err.message}. Please convert the image to JPG or PNG and try again.`
            )
          );
        } else {
          onError(
            new Error(
              "There was an error uploading the picture. Please try again later or contact support."
            )
          );
        }

        setUploadedImages([]);
        setIsUploading(false);
      });
  };

  return (
    <>
      {isUploading &&
        uploadedImages.map((image: any) => (
          <ImageWrapper
            key={image.metaData.path}
            $progress={100 - parseInt(image.progress)}
          >
            <PostImage src={image.preview} />
            <Loader location="center" size={24} color="#0063FB" />
          </ImageWrapper>
        ))}
      <UploadZone
        accept="image/*"
        isUploading={isUploading}
        onDrop={onDrop}
        onCancel={onCancel}
        openFileModalByDefault={openFileModalByDefault}
      />
    </>
  );
};

export default ImageUpload;
